import { Alert, IconButton, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../api/hooks/useAPI";
import { useCheckUserPermission } from "../api/hooks/useCheckUserPermission";
import useColors from "../api/hooks/useColors";
import { BootstrapContext } from "../contexts/BootstrapContext";
import { CustomerFeatureFlagsContext } from "../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../icons/SvgToIcon";
import {
  icons,
  severities,
} from "../scenes/stack/environment/icons/iconExport";
import { PLATFORM_USER_ACTIONS } from "../utils/constants";
import ConfirmationDialog from "./dialogs/ConfirmationDialog";
import { Toast } from "./Toast";

const InitialScanBanner = ({ showFullMessage = true }) => {
  const colors = useColors();
  const navigate = useNavigate();
  const { trackEvent, runNightlyFlowUI } = useAPI();

  const [openDialog, setOpenDialog] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  const { showNightlyFlow } = useContext(CustomerFeatureFlagsContext);
  const { user: platformUserData } = useContext(BootstrapContext);

  const hasPermission = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.SCAN_CONTROLS
  );

  if (!showNightlyFlow || !showBanner || !platformUserData?.show_nightly_flow) {
    return null;
  }

  const handleButtonClick = () => {
    trackEvent("User clicked run initial scan button");
    setOpenDialog(true);
  };

  const handleConfirm = async () => {
    trackEvent("User confirmed run initial scan");
    runNightlyFlowUI().then((result) => {
      Toast.info(
        "Running initial scan in the background. You'll be notified when it's done."
      );
      setShowBanner(false);
    });
    setOpenDialog(false);
  };

  const handleNavigateToIntegrations = () => {
    trackEvent("User clicked integrate more tools button");
    setOpenDialog(false);
    navigate("/inventory/integrations");
  };

  const handleDialogClose = () => {
    trackEvent("User closed run initial scan dialog");
    setOpenDialog(false);
  };

  const handleClose = () => {
    trackEvent("User dismissed run initial scan banner");
    setShowBanner(false);
  };

  // Create the action button for users with permission
  const actionButton = hasPermission ? (
    <IconButton
      sx={{
        color: colors.primary_enabled_text,
        height: "32px",
        width: "auto",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "0 8px",
        borderRadius: "8px",
        textTransform: "none",
        whiteSpace: "nowrap",
        "&:hover": {
          backgroundColor: colors.primary_4percent,
          borderColor: colors.primary_48percent,
        },
      }}
      onClick={handleButtonClick}
    >
      <>
        <SvgToIcon
          IconLogo={icons.ScanIcon}
          style={{ width: "18px", color: colors.primary_enabled_text }}
        />
        <Typography
          variant="overline"
          sx={{ textTransform: "none", fontWeight: "500" }}
        >
          Run Initial Scan
        </Typography>
      </>
    </IconButton>
  ) : null;

  return (
    <>
      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: colors.primary_4percent,
          color: colors.text_primary,
          borderRadius: "8px",
        }}
        onClose={handleClose}
        action={actionButton}
        icon={
          <SvgToIcon
            IconLogo={severities.SeverityBackgroundInfoIcon}
            width="24px"
            style={{
              color: colors.info_main,
            }}
          />
        }
      >
        {showFullMessage ? "A new integration was connected." : ""}
        {hasPermission
          ? " You can start a scan immediately, or wait for tomorrow's automated scan to see the results."
          : " Contact your administrator to initiate a scan."}
      </Alert>

      <ConfirmationDialog
        openDialog={openDialog}
        handleDialogClose={handleDialogClose}
        handleDialogCancel={handleNavigateToIntegrations}
        handleConfirm={handleConfirm}
        dialogTitle="Run initial scan?"
        dialogContentFirstLine="This scan takes about 1 hour to complete."
        dialogContentSecondLine="If there are more tools you'd like connect, integrate them now to include them in this scan."
        dialogContentThirdLine="Or just wait — the system will scan overnight and show results tomorrow."
        confirmationButtonText="Run Scan Now"
        cancelButtonText="Integrate More Tools"
      />
    </>
  );
};

export default InitialScanBanner;
